import React, { useState } from 'react';
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import contratoVentaService, { ContratoPeriodo } from '../../../services/contratoVentaService';
import { formatCurrency, formatDate, formatNroDocumentoVenta } from '../../../utils/formatters';

interface ContratoPeriodosTabProps {
  contratoId: number;
  periodos: ContratoPeriodo[];
  onUpdated: (periodos: ContratoPeriodo[]) => void;
  readOnly?: boolean;
}

const estadoColor = (estado: string): 'default' | 'success' | 'warning' | 'error' => {
  if (estado === 'Facturado') return 'success';
  if (estado === 'Pendiente') return 'warning';
  if (estado === 'ParcialmenteDevuelto') return 'error';
  return 'default';
};

const cellSx = {
  py: 0.5,
  px: 0.75,
  fontSize: '0.78rem',
  borderColor: 'divider',
} as const;

const headerCellSx = {
  ...cellSx,
  fontWeight: 600,
  fontSize: '0.72rem',
  bgcolor: 'action.hover',
  whiteSpace: 'nowrap',
} as const;

const etiquetaFactura = (p: ContratoPeriodo): string =>
  formatNroDocumentoVenta(p.facturaLetra, p.facturaNroSuc, p.facturaNroComprobante) || '—';

const ContratoPeriodosTab: React.FC<ContratoPeriodosTabProps> = ({
  contratoId,
  periodos,
  onUpdated,
  readOnly,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [editingOc, setEditingOc] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const handleSaveOc = async (periodo: ContratoPeriodo) => {
    try {
      setSavingId(periodo.id);
      const updated = await contratoVentaService.updatePeriodo(
        contratoId,
        periodo.id,
        editingOc[periodo.id] ?? periodo.nroOrdenCompra ?? undefined,
      );
      onUpdated(periodos.map((p) => (p.id === periodo.id ? { ...p, ...updated } : p)));
      enqueueSnackbar('OC del período actualizada', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar OC', { variant: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  if (periodos.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
        Todavía no hay períodos generados. Guardá el contrato para generar las cuotas.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 880 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Cuota</TableCell>
              <TableCell sx={headerCellSx}>Desde</TableCell>
              <TableCell sx={headerCellSx}>Hasta</TableCell>
              <TableCell sx={headerCellSx}>Facturación</TableCell>
              <TableCell sx={headerCellSx}>OC</TableCell>
              <TableCell sx={headerCellSx}>Estado</TableCell>
              <TableCell sx={headerCellSx} align="right">
                Esperado
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                Facturado
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                Pendiente
              </TableCell>
              <TableCell sx={headerCellSx}>Factura</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periodos.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell sx={cellSx}>{p.nroCuota}</TableCell>
                <TableCell sx={cellSx}>{formatDate(String(p.fechaDesde).slice(0, 10))}</TableCell>
                <TableCell sx={cellSx}>{formatDate(String(p.fechaHasta).slice(0, 10))}</TableCell>
                <TableCell sx={cellSx}>{formatDate(String(p.fechaFacturacion).slice(0, 10))}</TableCell>
                <TableCell sx={cellSx}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                      size="small"
                      value={editingOc[p.id] ?? p.nroOrdenCompra ?? ''}
                      onChange={(e) => setEditingOc((prev) => ({ ...prev, [p.id]: e.target.value }))}
                      disabled={readOnly}
                      sx={{
                        width: 110,
                        '& .MuiOutlinedInput-root': { height: 28 },
                        '& .MuiOutlinedInput-input': { py: '2px', px: '6px', fontSize: '0.78rem' },
                      }}
                    />
                    {!readOnly && (
                      <Tooltip title="Guardar OC">
                        <span>
                          <IconButton
                            size="small"
                            disabled={savingId === p.id}
                            onClick={() => handleSaveOc(p)}
                          >
                            <SaveIcon sx={{ fontSize: '0.95rem' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Chip
                    size="small"
                    label={p.estado}
                    color={estadoColor(p.estado)}
                    sx={{ height: 20, fontSize: '0.68rem' }}
                  />
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  {formatCurrency(p.importeEsperado)}
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  {formatCurrency(p.importeFacturadoNeto)}
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  {formatCurrency(p.importePendiente)}
                </TableCell>
                <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                  {etiquetaFactura(p)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ContratoPeriodosTab;
